import type { HttpContext } from '@adonisjs/core/http'

export const ODataResponseFormat = ['xml' as 'xml', 'json' as 'json']
export type ODataResponseFormat = (typeof ODataResponseFormat)[number]

export const negotiateContent = (
  ctx: HttpContext,
  fallback: ODataResponseFormat = 'json'
): ODataResponseFormat => {
  for (const acceptable of ODataResponseFormat) {
    const query = ctx.request.qs()
    if ('string' === typeof query['$format'] && query['$format'].includes(acceptable)) {
      return acceptable
    }
    if (ctx.request.header('accept')?.includes(acceptable)) {
      return acceptable
    }
  }
  return fallback
}

export const ODataMetadataLevel = ['full' as 'full', 'minimal' as 'minimal', 'none' as 'none']
export type ODataMetadataLevel = (typeof ODataMetadataLevel)[number]

export const negotiateMetadata = (
  ctx: HttpContext,
  fallback: ODataMetadataLevel = 'minimal'
): ODataMetadataLevel => {
  for (const acceptable of ODataMetadataLevel) {
    const query = ctx.request.qs()
    if (
      'string' === typeof query['$format'] &&
      query['$format'].includes(`metadata=${acceptable}`)
    ) {
      return acceptable
    }
    if (ctx.request.header('accept')?.includes(`metadata=${acceptable}`)) {
      return acceptable
    }
  }
  return fallback
}

export const ODataPreference = ['representation' as 'representation', 'minimal' as 'minimal']
export type ODataPreference = (typeof ODataPreference)[number]

export const negotiatePreference = (
  ctx: HttpContext,
  fallback: ODataPreference = 'representation'
): ODataPreference => {
  const prefer = ctx.request.header('prefer')
  if (prefer) {
    for (const acceptable of ODataPreference) {
      if (prefer.includes(acceptable)) {
        return acceptable
      }
    }
  }
  return fallback
}

const IEEE754_COMPATIBLE_TRUE = /ieee754compatible\s*=\s*true/i
const IEEE754_COMPATIBLE_FALSE = /ieee754compatible\s*=\s*false/i

/**
 * Negotiates the OData JSON `IEEE754Compatible` format parameter for a request.
 *
 * Per OData JSON §3.2, when `IEEE754Compatible=true` is negotiated, `Edm.Int64`
 * and `Edm.Decimal` values MUST be serialized as JSON strings (to preserve
 * precision in JavaScript / Power BI / Excel); when `false`, they are
 * serialized as JSON numbers. The server MUST NOT advertise a parameter it
 * does not actually conform to — mismatching the advertised parameter and the
 * actual payload is what triggers the Power BI
 * `DataSource.Error: Cannot convert a value to target type 'Edm.Int64' because
 * of conflict between input format string/number and parameter
 * 'IEEE754Compatible' false/true`.
 *
 * The fallback defaults to `false` (the OData spec default when the client
 * omits the parameter). Power BI and other OData clients that do not
 * explicitly request `IEEE754Compatible=true` reject unsolicited `=true`
 * responses — they expect JSON numbers for `Edm.Int64` / `Edm.Decimal` and
 * choke on strings they did not ask for. The controller's reverse-coercion
 * helper (`coerceIeee754NumericValues`) converts node-postgres `bigint` /
 * `numeric` string values back to JSON numbers when `=false`, so the wire
 * format stays consistent with the advertised parameter. Clients that
 * explicitly request `=true` via `Accept` / `$format` still get string
 * serialization.
 */
export const negotiateIeee754Compatible = (
  ctx: HttpContext,
  fallback: boolean = false
): boolean => {
  const query = ctx.request.qs()
  const format = typeof query['$format'] === 'string' ? query['$format'] : ''
  if (IEEE754_COMPATIBLE_TRUE.test(format)) return true
  if (IEEE754_COMPATIBLE_FALSE.test(format)) return false
  const accept = ctx.request.header('accept') || ''
  if (IEEE754_COMPATIBLE_TRUE.test(accept)) return true
  if (IEEE754_COMPATIBLE_FALSE.test(accept)) return false
  return fallback
}
